import { Module } from "vuex";
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { serializer } from "@/api/shared/serializer";
import moment from "moment";
import { getWhereIdInIds } from "@/api/shared/queryHelper";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";
import { ResidentStatus } from "@/enums/ResidentStatus";

const adHocActivitiesModule: Module<any, any> = {
  namespaced: false,
  state: {
    adHocActivities: [],
    adHocActivitiesTypes: [],
    adHocActivitiesReportData: {},
    residentsAdHocActivitiesReportData: {}
  },

  mutations: {
    setAdHocActivitiesDashboardReport(state: any, payload: []) {
      state.adHocActivitiesDashboardReport = payload;
    },
    setAdHocActivitiesTypes(state: any, payload: []) {
      state.adHocActivitiesTypes = payload;
    },
    setAdHocActivitiesReportData(state: any, payload: []) {
      state.adHocActivitiesReportData = payload;
    },
    setResidentsAdHocActivitiesReportData(state: any, payload: []) {
      state.residentsAdHocActivitiesReportData = payload;
    },
  },

  actions: {
    async getAdHocActivitiesDashboardReport({ commit }, data) {

      const facility = this.state.facilityModule.selectedFacility;

      const allResidents = this.state.residentModule.residentsShortData

      const residentId = data.residentId || null

      let adHocActivities = []

      if (residentId) {
        adHocActivities = serializer.querySnapshotToDocuments<any>(
          await firebase.firestore().collection("adHocActivities")
            .where('facilityId', '==', this.state.authModule.currentFacility)
            .where('residentId', '==', residentId)
            .where("start", ">", data.fromDate)
            .where("start", "<", data.toDate)
            .get());
      }
      else {
        adHocActivities = serializer.querySnapshotToDocuments<any>(
          await firebase.firestore().collection("adHocActivities")
            .where('facilityId', '==', this.state.authModule.currentFacility)
            .where("start", ">", data.fromDate)
            .where("start", "<", data.toDate)
            .get());
      }


      adHocActivities.forEach((x) => {
        x.startTimeDisplay = moment(x.start.seconds * 1000).format(facility?.timeFormat);
        x.endTimeDisplay = moment(x.end.seconds * 1000).format(facility?.timeFormat);
      });

      const adHocActivitiesResidentIds = [...new Set(adHocActivities.map(x => x.residentId).filter(Boolean))];

      let residents = []

      if (residentId) {
        residents = [serializer.documentSnapshotToDocument<any>(await firebase.firestore().collection("residents").doc(residentId).get())]
      }
      else {
        residents = await getWhereIdInIds(adHocActivitiesResidentIds, 'residents', this.state.authModule.currentFacility)
      }

      let residentsWithActivities = []

      for (const resident of residents) {
        const count = adHocActivities.filter(x => x.residentId === resident.id)?.length
        residentsWithActivities.push({ residentId: resident.id, residentName: resident.name, imageUrl: resident.imageUrl, count: count })
      }

      residentsWithActivities = residentsWithActivities.sort((a, b) => b.count - a.count);

      const residentsWithoutAdHocActivities = allResidents
        .filter(resident => !residentsWithActivities.some(activity => activity.residentId === resident.id))
        .map(x => ({ residentId: x.id, residentName: x.name, imageUrl: x.imageUrl, count: 0 }));

      const report: any = {
        mostActivitiesRecorded: residentsWithActivities,
        leastActivitiesRecorded: residentsWithoutAdHocActivities,
        adHocActivities: adHocActivities
      };

      if (residentId) {
        report.resident = residents[0]
      }

      commit("setAdHocActivitiesDashboardReport", report);

      return report
    },

    async getAdHocActivitiesReportData({ commit, dispatch }, { startDate = moment(), endDate = moment(), residentId = null }) {

      const facility = this.state.facilityModule.selectedFacility

      const dashboardReport = await dispatch('getAdHocActivitiesDashboardReport', { fromDate: startDate, toDate: endDate, residentId: residentId })

      const residents = dashboardReport.leastActivitiesRecorded.concat(dashboardReport.mostActivitiesRecorded)

      const activities = dashboardReport.adHocActivities

      const report: any = {
        items: [],
        activities: [],
        residents: residents
      };

      if (residentId) {
        report.resident = dashboardReport.resident
      }

      if (activities.length > 0) {

        const residentActivity = residents.filter(x => x.count > 0)

        report.activities = activities.map((a: any) => {
          const activity = a
          activity.start = moment(activity.start.seconds * 1000).toDate()
          activity.end = moment(activity.end.seconds * 1000).toDate()
          return activity;
        });

        residentActivity.forEach((resident: any) => {

          const residentActivities = activities.filter((x: any) => x.residentId === resident.residentId)

          residentActivities.forEach((activity: any) => {

            const startDateFormatted = getFormattedDateTimeString(activity.start, facility.dateFormat, facility.timeFormat, false)
            const endDateFormatted = getFormattedDateTimeString(activity.end, facility.dateFormat, facility.timeFormat, false)

            report.items.push({
              activityId: activity.id,
              residentId: resident.residentId,
              type: activity.type,
              residentName: resident.residentName,
              imageUrl: activity.imageUrl,
              startDisplay: startDateFormatted,
              endDisplay: endDateFormatted
            });
          });

        });

      }

      commit("setAdHocActivitiesReportData", report);
    },

    async getAdHocActivitiesTypes({ commit }) {
      const snap = await firebase.firestore().collection("adHocActivitiesTypes").where('facilityId', '==', this.state.authModule.currentFacility).get()
      const types = serializer.querySnapshotToDocuments<any>(snap)
      commit("setAdHocActivitiesTypes", types);
    },

    async getResidentsAdhocActivitiesReportData(
      { commit },
      { startDate, endDate }
    ) {

      const adHocActivities = serializer.querySnapshotToDocuments<any>(
        await firebase.firestore().collection("adHocActivities")
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where("start", ">", startDate)
          .where("start", "<", endDate)
          .get());

      const report = {
        residents: []
      };

      if (adHocActivities.length > 0) {

        let residentsData = await this.dispatch("getResidentsShortData", ResidentStatus.ActiveResident);

        const activitiesData = adHocActivities.map((a: any) => {
          const activity = a
          activity.start = moment(activity.start.seconds * 1000).toDate();
          activity.end = moment(activity.end.seconds * 1000).toDate();
          return activity;
        });

        //set report data for each resident
        residentsData.forEach(resident => {

          const residentActivities = activitiesData.filter((x: any) => x.residentId === resident.id)

          resident.activitiesCount = residentActivities.length;

          //normalize status
          resident.statusDisplay = resident.status.replace(" Resident", "");

          //get average number of events per day per selected date range
          const result = residentActivities.reduce(function (totals: any, activity: any) {
            const day = moment(activity.start).format("DD/MM/YYYY");
            !totals[day] ? (totals[day] = 1) : (totals[day] = totals[day] + 1);

            return totals;
          }, {});
          const dayEventsCountArr: number[] = Object.values(result);
          const sum = dayEventsCountArr.reduce((a, b) => a + b, 0);
          resident.activitiesPerDay =
            dayEventsCountArr.length > 0
              ? Math.ceil(sum / dayEventsCountArr.length)
              : 0;
        });

        // sort residents by lifestyle events number
        residentsData = residentsData.sort((a, b) => {
          return a.activitiesCount - b.activitiesCount;
        });

        //set report data
        report.residents = residentsData.map((x) => {
          return {
            id: x.id,
            statusDisplay: x.statusDisplay,
            name: x.name,
            activitiesCount: x.activitiesCount,
            activitiesPerDay: x.activitiesPerDay,
          }
        })
      }

      commit("setResidentsAdHocActivitiesReportData", report);
    },

    async addAdHocActivityType({ dispatch }, data) {

      if (!data) return

      try {

        data.facilityId = this.state.authModule.currentFacility

        const res = await firebase.firestore().collection("adHocActivitiesTypes").add(data)

        data.id = res.id

        await dispatch("getAdHocActivitiesTypes")

        return data

      } catch (error) {
        console.error(error)
        return null
      }

    },

    async updateAdHocActivityType({ dispatch }, data) {

      if (!data.id) return

      const updateData = {
        dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date()),
        name: data.name
      }

      return await firebase.firestore().collection("adHocActivitiesTypes").doc(data.id).update(updateData)
        .then(async () => {
          await dispatch("getAdHocActivitiesTypes")
          return updateData
        })
        .catch((error) => {
          console.error("Error updating ad hoc activity", error);
          return null
        })
    },

    async deleteAdHocActivityType({ dispatch }, id) {
      if (id?.length <= 0) return;

      try {
        await firebase.firestore().collection("adHocActivitiesTypes").doc(id).delete();
        await dispatch("getAdHocActivitiesTypes")
      } catch (error) {
        console.error("Error deleting ad hoc activity type", error);
      }
    },

    async isAdHocTypeInUse({ dispatch }, typeId) {
      const snap = await firebase.firestore().collection("adHocActivities")
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('typeId', '==', typeId).get()
      const activities = serializer.querySnapshotToDocuments<any>(snap)

      const records = activities.filter(x => x.typeId === typeId);

      return records
    },

    async addAdHocActivity({ dispatch }, data) {

      if (!data) return

      try {

        data.facilityId = this.state.authModule.currentFacility

        const res = await firebase.firestore().collection("adHocActivities").add(data)

        data.id = res.id

        await dispatch("getAdHocActivities")

        return data

      } catch (error) {
        console.error(error)
        return null
      }

    },

    async updateAdHocActivities({ dispatch }, data) {

      if (!data.id) return

      const updateData = {
        dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date()),
        ...data
      }

      return await firebase.firestore().collection("adHocActivities").doc(data.id).update(updateData)
        .then(async () => {
          await dispatch("getAdHocActivities")
          return updateData
        })
        .catch((error) => {
          console.error("Error updating ad hoc activity", error);
          return null
        })
    },

    async deleteAdHocActivity({ dispatch }, id) {
      if (id?.length <= 0) return;

      try {
        await firebase.firestore().collection("adHocActivities").doc(id).delete();
        await dispatch("getAdHocActivities")
      } catch (error) {
        console.error("Error deleting ad hoc activities", error);
      }
    }
  }
};
export default adHocActivitiesModule;